import { useCallback } from 'react'
import { useGlobalStore } from '@highlight/ipc/react'
import { GlobalStore } from '@highlight/ipc'
import { useProfile } from './useProfile'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'

export type IncognitoSource = 'mini-menu' | 'mini-notch' | 'mini-pill' | 'sidepanel-toolbar'

const deriveDurationLabel = (duration?: number): '1_hour' | 'until_morning' | 'indefinite' => {
  if (duration === undefined) return 'indefinite'
  if (duration === 3600000) return '1_hour'
  return 'until_morning'
}

export const useIncognitoMode = () => {
  const [incognitoMode, setIncognitoMode] = useGlobalStore<boolean>('general', 'incognitoMode', false)
  const [incognitoModeTimer, setIncognitoModeTimer] = useGlobalStore<number | undefined>(
    'general',
    'incognitoModeTimer',
  )
  const [, setDataPrivacyBeforeIncognito] = useGlobalStore<boolean | undefined>(
    'general',
    'dataPrivacyModeBeforeIncognito',
  )
  const { profileData, updateProfile } = useProfile()

  const enable = useCallback(
    async (duration?: number, source?: IncognitoSource) => {
      const alreadySaved = GlobalStore.general().get<boolean | undefined>('dataPrivacyModeBeforeIncognito')
      const didSave = alreadySaved === undefined
      if (didSave) {
        setDataPrivacyBeforeIncognito(profileData?.dataPrivacyModeEnabled ?? false)
      }

      // Optimistic update
      setIncognitoMode(true)
      const timerValue = duration ? Date.now() + duration : undefined
      setIncognitoModeTimer(timerValue)

      try {
        await updateProfile({ dataPrivacyModeEnabled: true })
        AmplitudeRenderer.trackEvent('Incognito Mode', {
          action: 'enable',
          duration: deriveDurationLabel(duration),
          source,
        })
      } catch (error) {
        // Roll back on failure
        setIncognitoMode(false)
        setIncognitoModeTimer(undefined)
        if (didSave) {
          setDataPrivacyBeforeIncognito(undefined)
        }
        throw error
      }
    },
    [updateProfile, setIncognitoMode, setIncognitoModeTimer, setDataPrivacyBeforeIncognito, profileData],
  )

  const disable = useCallback(
    async (source?: IncognitoSource) => {
      const previousValue = GlobalStore.general().get<boolean | undefined>('dataPrivacyModeBeforeIncognito')
      const previousTimer = GlobalStore.general().get<number | undefined>('incognitoModeTimer')

      // Optimistic update
      setIncognitoMode(false)
      setIncognitoModeTimer(undefined)
      setDataPrivacyBeforeIncognito(undefined)

      try {
        await updateProfile({ dataPrivacyModeEnabled: previousValue ?? false })
        AmplitudeRenderer.trackEvent('Incognito Mode', {
          action: 'disable',
          source,
        })
      } catch (error) {
        // Roll back on failure
        setIncognitoMode(true)
        setIncognitoModeTimer(previousTimer)
        if (previousValue !== undefined) {
          setDataPrivacyBeforeIncognito(previousValue)
        }
        throw error
      }
    },
    [updateProfile, setIncognitoMode, setIncognitoModeTimer, setDataPrivacyBeforeIncognito],
  )

  return {
    isIncognitoEnabled: incognitoMode,
    enableIncognito: enable,
    disableIncognito: disable,
    incognitoTimer: incognitoModeTimer,
  }
}
